import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Clock, Check, AlertTriangle, ChevronRight, FileText } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { getMyHistory } from '../../lib/api';
import { getUserName } from '../../lib/auth';
import { toast } from 'sonner';
import DriverLayout from '../../components/DriverLayout';

export default function HistoryPage() {
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCheck, setSelectedCheck] = useState(null);
  const userName = getUserName();

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const response = await getMyHistory();
      setChecks(response.data);
    } catch (error) {
      toast.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return format(new Date(dateString), 'dd MMM yyyy, HH:mm');
  };

  if (loading) {
    return (
      <DriverLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </DriverLayout>
    );
  }

  return (
    <DriverLayout>
      <div className="pb-20">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-blue-900">Check History</h1>
          <p className="text-slate-500">{userName}'s submitted checks</p>
        </div>

        {checks.length === 0 ? (
          <div className="empty-state" data-testid="empty-history">
            <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <p className="text-lg font-medium text-slate-600">No checks submitted yet</p>
            <p className="text-slate-400">Your completed vehicle checks will appear here</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="history-list">
            {checks.map((check, index) => (
              <Card 
                key={check.id}
                className="cursor-pointer card-hover border-slate-200"
                onClick={() => setSelectedCheck(check)}
                data-testid={`history-item-${index}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono font-bold text-blue-900">
                          {check.vehicle_registration}
                        </span>
                        {check.has_issues ? (
                          <Badge variant="destructive" className="text-xs">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Issues
                          </Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-800 text-xs">
                            <Check className="w-3 h-3 mr-1" />
                            All Clear
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-sm text-slate-500">
                        <Clock className="w-4 h-4" />
                        {formatDate(check.submitted_at)}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Check Detail Dialog */}
        <Dialog open={!!selectedCheck} onOpenChange={() => setSelectedCheck(null)}>
          <DialogContent className="max-w-lg max-h-[85vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span className="font-mono">{selectedCheck?.vehicle_registration}</span>
                {selectedCheck?.has_issues ? (
                  <Badge variant="destructive">Issues Found</Badge>
                ) : (
                  <Badge className="bg-green-100 text-green-800">All Clear</Badge>
                )}
              </DialogTitle>
            </DialogHeader>
            
            {selectedCheck && (
              <ScrollArea className="max-h-[60vh]">
                <div className="space-y-4">
                  <div className="text-sm text-slate-500">
                    Submitted: {formatDate(selectedCheck.submitted_at)}
                  </div>

                  {/* Checklist Results */}
                  <div>
                    <h4 className="font-semibold mb-2">Checklist Results</h4>
                    <div className="space-y-1">
                      {selectedCheck.checklist.map((item, idx) => (
                        <div 
                          key={idx}
                          className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0"
                        >
                          <span className="text-sm">{item.item}</span>
                          <Badge 
                            className={
                              item.status === 'pass' ? 'status-pass' :
                              item.status === 'fail' ? 'status-fail' : 'status-na'
                            }
                          >
                            {item.status.toUpperCase()}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Defects */}
                  {selectedCheck.defects && (
                    <div>
                      <h4 className="font-semibold mb-2 text-red-700">Defects Reported</h4>
                      <p className="text-sm bg-red-50 p-3 rounded-lg text-red-800">
                        {selectedCheck.defects}
                      </p>
                    </div>
                  )}

                  {/* Comments */}
                  {selectedCheck.comments && (
                    <div>
                      <h4 className="font-semibold mb-2">Comments</h4>
                      <p className="text-sm bg-slate-50 p-3 rounded-lg">
                        {selectedCheck.comments}
                      </p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DriverLayout>
  );
}
